<template>
    <div class="bundle-template-summary">
        <v-list
            class="d-flex flex-column rounded-lg border-thin py-0"
            :lines="false"
        >
            <template
                v-for="(component, index) in sortedComponents"
                :key="`bundle-summary-${component.lineUid}-${index}`"
            >
                <v-list-item class="py-3">
                    <div class="d-flex align-center">
                        <span class="text-uppercase font-weight-bold font-size-7">
                            {{ component.name }}
                        </span>
                        <v-spacer></v-spacer>
                        <div class="d-flex align-center">
                            <span
                                v-if="getDescription(component)"
                                class="font-size-8 mr-2"
                            >
                                {{ getDescription(component) }}
                            </span>
                            <ColorSwatch
                                v-if="getColors(component).length"
                                :color="getColors(component)"
                                disable-border
                            ></ColorSwatch>
                        </div>
                    </div>
                </v-list-item>
                <v-divider
                    v-if="index < sortedComponents.length - 1"
                ></v-divider>
            </template>
        </v-list>
    </div>
</template>

<script lang="ts" setup>
    import ColorSwatch from '~/components/color/ColorSwatch/ColorSwatch.vue';

    import type {
        Bundle,
        BundleComponent,
        AttributeValue
    } from '~/types';

    const props = defineProps<{
        bundle: Bundle;
    }>();

    const sortedComponents = computed(() =>
        [...props.bundle.components].sort((a, b) => a.sortOrder - b.sortOrder)
    );

    function getColorName(component: BundleComponent): string | null {
        if (component.sku?.options) {
            const colorOpt = (component.sku.options as AttributeValue[]).find((o) => o.colors?.length);
            if (colorOpt) return colorOpt.name;
        }
        const option = component.option && typeof component.option === 'object'
            ? component.option as AttributeValue
            : null;
        if (option?.colors?.length) return option.name;
        return null;
    }

    function getDescription(component: BundleComponent): string | null {
        const parts: string[] = [];
        if (component.description) parts.push(component.description);
        else {
            const option = component.option && typeof component.option === 'object'
                ? component.option as AttributeValue
                : null;
            if (option?.name) parts.push(option.name);
        }
        const colorName = getColorName(component);
        if (colorName) parts.push(colorName);
        return parts.length ? parts.join(', ') : null;
    }

    function getColors(component: BundleComponent): string[] {
        // SKU lines: color from the SKU's color option
        if (component.sku?.options) {
            const colorOpt = (component.sku.options as AttributeValue[]).find((o) => o.colors?.length);
            if (colorOpt) return colorOpt.colors!;
        }
        // Option lines: color from the option itself
        const option = component.option && typeof component.option === 'object'
            ? component.option as AttributeValue
            : null;
        if (option?.colors?.length) return option.colors;
        return [];
    }
</script>

<style lang="scss"></style>
